import { useState, useEffect, useCallback } from "react";
import { getCrewEvents, sendIndividualCheck, unassignCrewFromEvent, assignCrewToEvent, listEvents, overrideCrewStatus } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { CrewEventRow } from "./CrewEventRow";

function formatDateRange(start, end) {
  if (!start) return "";
  const s = new Date(start + "T00:00:00");
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (!end || start === end) return fmt(s);
  return `${fmt(s)} - ${fmt(new Date(end + "T00:00:00"))}`;
}

export function CrewCardEvents({ crewId, canEdit, onUpdate }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState({});
  const [assignOpen, setAssignOpen] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await getCrewEvents(crewId);
      setEvents(res.data);
    } catch (err) { toast.error("Failed to load crew events"); }
    finally { setLoading(false); }
  }, [crewId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleSendCheck = async (eventId, isReminder = false) => {
    setSending(prev => ({ ...prev, [eventId]: true }));
    try {
      await sendIndividualCheck(eventId, crewId, { reminder: isReminder });
      toast.success(isReminder ? "Reminder sent" : "Availability check sent");
      fetchEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSending(prev => ({ ...prev, [eventId]: false })); }
  };

  const handleOverride = async (eventId, newStatus) => {
    try {
      const res = await overrideCrewStatus(eventId, crewId, newStatus);
      const smsNote = res.data.sms_sent ? " (SMS sent)" : "";
      toast.success(`Marked as ${newStatus}${smsNote}`);
      fetchEvents();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update status");
    }
  };

  const handleUnassign = async (eventId) => {
    try {
      await unassignCrewFromEvent(crewId, eventId);
      toast.success("Unassigned from event");
      fetchEvents();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to unassign");
    }
  };

  const handleAssign = async (eventId) => {
    setAssignLoading(true);
    try {
      await assignCrewToEvent(crewId, eventId);
      toast.success("Assigned to event");
      setAssignOpen(false);
      fetchEvents();
      onUpdate?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to assign");
    } finally { setAssignLoading(false); }
  };

  const openAssignPicker = async () => {
    setAssignOpen(true);
    try {
      const res = await listEvents();
      setAllEvents(res.data);
    } catch (err) { toast.error("Failed to load events"); }
  };

  const assignedIds = new Set(events.map(e => e.event_id));
  const availableEvents = allEvents
    .filter(e => !assignedIds.has(e.event_id) && e.status !== "cancelled" && e.status !== "declined")
    .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));

  if (loading) return <div className="mt-2 pt-2 border-t border-border"><Loader2 className="w-3 h-3 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid={`crew-events-${crewId}`} className="mt-2 pt-2 border-t border-border space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Assigned Events ({events.length})
        </span>
        {canEdit && (
          <Popover open={assignOpen} onOpenChange={setAssignOpen}>
            <PopoverTrigger asChild>
              <Button data-testid={`assign-event-btn-${crewId}`} variant="ghost" size="sm" className="h-5 text-[10px] text-primary px-1.5" onClick={openAssignPicker}>
                <Plus className="w-3 h-3 mr-0.5" />Assign
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 max-w-[calc(100vw-2rem)] p-0" align="end">
              <Command>
                <CommandInput placeholder="Search events..." className="h-8 text-xs" />
                <CommandList className="max-h-48">
                  <CommandEmpty className="text-xs py-3">No events found</CommandEmpty>
                  {availableEvents.map(ev => (
                    <CommandItem key={ev.event_id} data-testid={`assign-option-${ev.event_id}`} onSelect={() => handleAssign(ev.event_id)} disabled={assignLoading} className="text-xs cursor-pointer">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="font-medium truncate">{ev.name}</span>
                        <span className="text-muted-foreground text-[10px]">{formatDateRange(ev.start_date, ev.end_date)}{ev.venue ? ` · ${ev.venue}` : ""}</span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {events.length === 0 && <p className="text-[10px] text-muted-foreground/50 italic">No events assigned</p>}

      {events.map(ev => (
        <CrewEventRow
          key={ev.event_id}
          ev={ev}
          canEdit={canEdit}
          sending={sending[ev.event_id]}
          onSendCheck={handleSendCheck}
          onOverride={handleOverride}
          onUnassign={handleUnassign}
        />
      ))}
    </div>
  );
}
